import { DeleteOutlined } from "@ant-design/icons";
import cuid from "cuid";
import { FieldArray, Formik } from "formik";
import { defaultsDeep, get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiInfoCircle, BiPlus, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Switch from "shared/components/Switch";
import Tooltip from "shared/components/Tooltip";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import {
  ATTRIBUTION_TYPE,
  MATCHING_FIELDS,
  PERIOD_UNITS,
  PROCESS_CONFIG_TYPES,
  SKILL_TYPES,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";

const MAX_MATCHING_RULES = 5;

const DAYS_PER_UNIT = { days: 1, months: 30, years: 365 };

const lookbackDaysToDisplay = (lookbackDays) => {
  if (lookbackDays % 365 === 0)
    return { value: lookbackDays / 365, unit: "years" };
  if (lookbackDays % 30 === 0)
    return { value: lookbackDays / 30, unit: "months" };
  return { value: lookbackDays, unit: "days" };
};

const getDefaultPreviousAdvisorConfig = () => ({
  id: cuid(),
  type: PROCESS_CONFIG_TYPES.PREVIOUS_ADVISOR_CONFIG,
  value: {
    enabled: true,
    matchingRules: [["email"], ["phone"]],
    lookbackDays: 365,
  },
});

const MATCHING_FIELD_OPTIONS = (FORM) =>
  Object.entries(MATCHING_FIELDS).map(([_key, value]) => ({
    label: get(FORM, `PREVIOUS_ADVISOR_CONFIG.MATCHING_FIELDS.${value}`, value),
    value,
  }));

const Skills = ({ formRef, dirtyRef, setIsDirty, TABS, onSubmit }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      initialValues={{
        skills: [],
        attributionType: {
          id: cuid(),
          type: PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
          value: null,
        },
        // previousAdvisorConfig: {
        //   id: cuid(),
        //   type: PROCESS_CONFIG_TYPES.PREVIOUS_ADVISOR_CONFIG,
        //   value: {
        //     enabled: true,
        //     matchingRules: [["email"], ["phone"]],
        //   },
        // },
      }}
      onSubmit={async (values) => {
        await onSubmit({
          type: "",
          name: "",
          theme: null,
          description: "",
          skills: values.skills,
          config: [
            ...(values.attributionType.value ? [values.attributionType] : []),
            values.previousAdvisorConfig,
          ],
          attributionType: values.attributionType,
          processStatuses: [],
          insurancePlans: [],
        });
        setIsDirty((prev) => ({ ...prev, [TABS.SKILLS]: false }));
      }}
    >
      {({ values: skillsValues, setFieldValue, dirty, ...skillsFormProps }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({ ...prev, [TABS.SKILLS]: dirty }));
        }

        const previousAdvisorEnabled = get(
          skillsValues,
          "previousAdvisorConfig.value.enabled",
          false,
        );
        const matchingRules = get(
          skillsValues,
          "previousAdvisorConfig.value.matchingRules",
          [],
        );

        return (
          <Card title={FORM.SKILLS.TITLE} divider={false} head={false}>
            <div className="grid--1">
              <div className="grid--2">
                <div>
                  <div className="d-flex align--center gap-4 mb-4">
                    <label>{FORM.ATTRIBUTION_TYPE}</label>
                    <Tooltip
                      title={get(
                        FORM,
                        "PREVIOUS_ADVISOR_CONFIG.ATTRIBUTION_TYPE_INFO",
                        "",
                      )}
                    >
                      <BiInfoCircle size={14} className="c-gray" />
                    </Tooltip>
                  </div>
                  <Select
                    options={Object.entries(ATTRIBUTION_TYPE).map(
                      ([key, value]) => ({
                        label: FORM.ATTRIBUTION_OPTIONS[key],
                        value,
                      }),
                    )}
                    placeholder={FORM.ATTRIBUTION_TYPE}
                    {...bindInputProps({
                      name: "attributionType.value",
                      values: skillsValues,
                      ...skillsFormProps,
                    })}
                  />
                </div>
              </div>
              <div className="grid--1 mt-16">
                <p className="c-text mb-8">
                  {get(FORM, "PREVIOUS_ADVISOR_CONFIG.DESCRIPTION", "")}
                </p>
                <div className="d-flex align--center gap-8">
                  <Switch
                    checked={previousAdvisorEnabled}
                    onChange={(checked) => {
                      if (checked) {
                        const current = get(
                          skillsValues,
                          "previousAdvisorConfig",
                          {},
                        );
                        setFieldValue(
                          "previousAdvisorConfig",
                          defaultsDeep(
                            current,
                            getDefaultPreviousAdvisorConfig(),
                          ),
                        );
                        setFieldValue(
                          "previousAdvisorConfig.value.enabled",
                          true,
                        );
                      } else {
                        setFieldValue(
                          "previousAdvisorConfig.value.enabled",
                          false,
                        );
                      }
                      setIsDirty((prev) => ({ ...prev, [TABS.SKILLS]: true }));
                    }}
                  />
                  <span>
                    {get(
                      FORM,
                      "PREVIOUS_ADVISOR_CONFIG.TOGGLE_LABEL",
                      "Previous advisor priority",
                    )}
                  </span>
                </div>
                {previousAdvisorEnabled && (
                  <div className="grid--1 mt-8">
                    <div>
                      <div className="d-flex align--center gap-4 mb-4">
                        <label>
                          {get(
                            FORM,
                            "PREVIOUS_ADVISOR_CONFIG.LOOKBACK_LABEL",
                            "Lookback period",
                          )}
                        </label>
                        <Tooltip
                          title={get(
                            FORM,
                            "PREVIOUS_ADVISOR_CONFIG.LOOKBACK_INFO",
                            "",
                          )}
                        >
                          <BiInfoCircle size={14} className="c-gray" />
                        </Tooltip>
                      </div>
                      <div className="d-flex gap-8 align--end mb-8">
                        <Input
                          type="number"
                          value={
                            lookbackDaysToDisplay(
                              get(
                                skillsValues,
                                "previousAdvisorConfig.value.lookbackDays",
                                365,
                              ),
                            ).value
                          }
                          onChange={(e) => {
                            const num = parseInt(e.target.value, 10) || 1;
                            const { unit } = lookbackDaysToDisplay(
                              get(
                                skillsValues,
                                "previousAdvisorConfig.value.lookbackDays",
                                365,
                              ),
                            );
                            setFieldValue(
                              "previousAdvisorConfig.value.lookbackDays",
                              Math.max(1, num) * DAYS_PER_UNIT[unit],
                            );
                            setIsDirty((prev) => ({
                              ...prev,
                              [TABS.SKILLS]: true,
                            }));
                          }}
                          min={1}
                        />
                        <Select
                          options={Object.entries(PERIOD_UNITS).map(
                            ([key, value]) => ({
                              label: get(
                                FORM,
                                `PREVIOUS_ADVISOR_CONFIG.${key}`,
                                key,
                              ),
                              value,
                            }),
                          )}
                          value={
                            lookbackDaysToDisplay(
                              get(
                                skillsValues,
                                "previousAdvisorConfig.value.lookbackDays",
                                365,
                              ),
                            ).unit
                          }
                          onChange={(unit) => {
                            const { value: num } = lookbackDaysToDisplay(
                              get(
                                skillsValues,
                                "previousAdvisorConfig.value.lookbackDays",
                                365,
                              ),
                            );
                            setFieldValue(
                              "previousAdvisorConfig.value.lookbackDays",
                              num * DAYS_PER_UNIT[unit],
                            );
                            setIsDirty((prev) => ({
                              ...prev,
                              [TABS.SKILLS]: true,
                            }));
                          }}
                        />
                      </div>
                    </div>
                    <label className="d-flex align--center gap-4 mb-8">
                      {get(
                        FORM,
                        "PREVIOUS_ADVISOR_CONFIG.MATCHING_RULES",
                        "Matching rules",
                      )}
                      <Tooltip
                        title={get(
                          FORM,
                          "PREVIOUS_ADVISOR_CONFIG.MATCHING_RULES_INFO",
                          "",
                        )}
                      >
                        <BiInfoCircle size={14} className="c-gray" />
                      </Tooltip>
                    </label>
                    {matchingRules.map((rule, ruleIndex) => (
                      <div key={`rule-${ruleIndex}`}>
                        {ruleIndex > 0 && (
                          <div className="d-flex justify--center mb-8 mt-8">
                            <span className="c-gray">
                              {get(
                                FORM,
                                "PREVIOUS_ADVISOR_CONFIG.OR_SEPARATOR",
                                "OR",
                              )}
                            </span>
                          </div>
                        )}
                        <div className="d-flex align--center gap-8">
                          <div className="flex--grow-1">
                            <Select
                              mode="multiple"
                              options={MATCHING_FIELD_OPTIONS(FORM)}
                              placeholder={get(
                                FORM,
                                "PREVIOUS_ADVISOR_CONFIG.RULE_PLACEHOLDER",
                                "Select one or more criteria",
                              )}
                              value={rule}
                              onChange={(value) => {
                                setFieldValue(
                                  `previousAdvisorConfig.value.matchingRules.${ruleIndex}`,
                                  value,
                                );
                                setIsDirty((prev) => ({
                                  ...prev,
                                  [TABS.SKILLS]: true,
                                }));
                              }}
                            />
                          </div>
                          {matchingRules.length > 1 && (
                            <div
                              className="delete-icon"
                              onClick={() => {
                                const updatedRules = matchingRules.filter(
                                  (_, idx) => idx !== ruleIndex,
                                );
                                setFieldValue(
                                  "previousAdvisorConfig.value.matchingRules",
                                  updatedRules,
                                );
                                setIsDirty((prev) => ({
                                  ...prev,
                                  [TABS.SKILLS]: true,
                                }));
                              }}
                            >
                              <BiTrash color={COLORS.C_DANGER} size={20} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {matchingRules.length < MAX_MATCHING_RULES && (
                      <div className="mt-8">
                        <Button
                          type="primary--outlined"
                          onClick={() => {
                            setFieldValue(
                              "previousAdvisorConfig.value.matchingRules",
                              [...matchingRules, []],
                            );
                            setIsDirty((prev) => ({
                              ...prev,
                              [TABS.SKILLS]: true,
                            }));
                          }}
                        >
                          <BiPlus size={16} />{" "}
                          {get(
                            FORM,
                            "PREVIOUS_ADVISOR_CONFIG.ADD_RULE",
                            "Add rule",
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <FieldArray
                name={"skills"}
                render={(arrayHelpers) => [
                  get(skillsValues, "skills", []).map((_, i) => [
                    <div className="grid--2" key={`row-${i}`}>
                      <Select
                        options={Object.entries(SKILL_TYPES)
                          .map(([key, value]) => ({
                            label: get(FORM, `SKILLS.TYPES.${key}`, key),
                            value,
                          }))
                          .filter(
                            ({ value }) =>
                              value === get(skillsValues, `skills.${i}.type`) ||
                              get(skillsValues, "skills", []).every(
                                ({ type }) => type !== value,
                              ),
                          )}
                        placeholder={FORM.SKILLS.TYPE}
                        label={FORM.SKILLS.TYPE}
                        {...bindArrayInputProps({
                          parent: "skills",
                          index: i,
                          name: "type",
                          values: skillsValues,
                          ...skillsFormProps,
                        })}
                      />
                      <div className="d-flex align--end">
                        <Button
                          onClick={() => {
                            arrayHelpers.remove(i);
                          }}
                          type="danger--outlined"
                        >
                          <DeleteOutlined />
                        </Button>
                      </div>
                    </div>,
                  ]),
                  <Button
                    key="add-row"
                    onClick={() => {
                      arrayHelpers.push({
                        id: cuid(),
                        type: null,
                        value: null,
                      });
                    }}
                    type="primary"
                  >
                    <BiPlus size={24} />
                  </Button>,
                ]}
              />
            </div>
          </Card>
        );
      }}
    </Formik>
  );
};

export default Skills;
